import FilePreviewComponent from "@/components/probaworkflows_base_components/FilePreviewComponent/FilePreviewComponent.vue";
import {
  CREATE_CHECKLIST_EVALUATION_RESULT,
  GET_CHECKLIST_ITEMS_WITH_ANSWER_STATUS,
  GET_CHECKLIST_ITEM_CONDITION,
  START_ONGOING_COMPLIANCE,
  get_headers,
  get_user_role
} from '@/utils/constants/constants';
import alert_mixin from '@/utils/mixins/alert_mixin';
import onboarding_checklist_question_status_mixin from '@/utils/mixins/compliance_mixins/onboarding_checklist_question_status_mixin';
import axios from 'axios';
export default {
  name: 'ComplianceChecklist',
  mixins: [alert_mixin, onboarding_checklist_question_status_mixin],
  components: {
    FilePreviewComponent,
  },
  props: {
    entity_profile_id: {
      type: [String, Number],
      required: true
    }
  },
  data: () => ({
    checklist_items: [],
    is_loading: false,
    is_rerunning_checks: false,
    preview_modal: false,
    preview_url: null,
    preview_file_name: '',
    question_reason_visibility: {},
    user_role: null
  }),

  computed: {
    total_target_questions() {
      return this.checklist_items.reduce((sum, item) => {
        const questions = this.get_applicable_questions(Reflect.get(item, 'target_questions') || []);
        return sum + questions.length;
      }, 0);
    },

    answered_target_questions() {
      return this.checklist_items.reduce((sum, item) => {
        const questions = this.get_applicable_questions(Reflect.get(item, 'target_questions') || []);
        const answered_count = questions.filter(question => Reflect.get(question, 'is_answered') === true).length;
        return sum + answered_count;
      }, 0);
    },

    completed_count() {
      return this.checklist_items.filter(item => Reflect.get(item, 'is_completed') === true).length;
    },

    completion_percentage() {
      if (this.total_target_questions === 0) return 0;
      return Math.round((this.answered_target_questions / this.total_target_questions) * 100);
    },

    progress_bar_style() {
      const progress_bar_style = {
        width: `${this.completion_percentage}%`,
        '--hue': this.progress_bar_hue
      };
      return progress_bar_style;
    },

    progress_bar_hue() {
      return 120 * (this.completion_percentage / 100);
    },

    progress_color() {
      if (this.completion_percentage < 33) return 'red';
      if (this.completion_percentage < 66) return 'orange';
      return 'green';
    }
  },

  watch: {
    checklist_items: {
      deep: true,
      handler() {
        this.update_completion_status();
      }
    },
    entity_profile_id() {
      this.load_checklist_items();
    }
  },

  mounted() {
    this.load_checklist_items();

  },

  async created() {
      this.user_role = await get_user_role();
    },

  methods: {
    is_manual_requirement_pending(req_status) {
      if (!req_status || Reflect.get(req_status, 'is_met')) return false;

      const requirement_type = this.normalize_requirement_type(Reflect.get(req_status, 'type'));
      const is_manual_gate = ['answered', 'manual_verification'].includes(requirement_type);
      if (!is_manual_gate) return false;

      const details = Array.isArray(Reflect.get(req_status, 'details'))
        ? Reflect.get(req_status, 'details')
        : [];
      if (!details.length) return false;

      return details.every(detail =>
        String(Reflect.get(detail || {}, 'message') || '').toLowerCase().includes('manual approval')
      );
    },

    can_manual_check_item(item) {
      const check_mode = String(item?.check_mode || '').toLowerCase();
      if (check_mode === 'manual') {
        return true;
      }

      const statuses = Array.isArray(item?.requirement_statuses)
        ? item.requirement_statuses
        : [];

      return statuses.some(req_status => this.is_manual_requirement_pending(req_status));
    },

    is_manual_checked(item) {
      return item?.manual_checked === true || item?.completed_by === 'manual';
    },

    show_mark_as_evaluated(item) {
      if (!item) return false;

      const check_mode = String(Reflect.get(item, 'check_mode') || '').toLowerCase();
      if (check_mode === 'manual') {
        return this.is_manual_checked(item);
      }

      return Reflect.get(item, 'is_completed') === true;
    },

    async get_check_mode_for_association(association_id) {
      if (!association_id) {
        return 'automatic';
      }

      return axios
        .get(GET_CHECKLIST_ITEM_CONDITION, {
          headers: get_headers(),
          params: { checklist_item_association_id: association_id },
        })
        .then(response => String(response?.data?.data?.check_mode || 'automatic').toLowerCase())
        .catch(() => 'automatic');
    },

    async load_checklist_items() {
      if (!this.entity_profile_id) {
        return;
      }

      if (isNaN(Number(this.entity_profile_id))) {
        return;
      }

      this.is_loading = true;
      try {
        const response = await axios.get(GET_CHECKLIST_ITEMS_WITH_ANSWER_STATUS, {
          headers: get_headers(),
          params: { entity_id: this.entity_profile_id }
        });
        if (response.data.status === 'success') {
          const mapped_items = response.data.data.map(item => {
            const target_questions = item.target_questions.map(question => ({
              association_id: question.association_id,
              id: question.id,
              text: question.question,
              question_type: question.question_type,
              check_mode: question.check_mode || 'automatic',
              is_answered: question.is_answered,
              answer: question.answer || null,
              is_applicable: question.is_applicable !== false,
              dependency_meta: question.dependency_meta || null
            }));

            const has_manual_mode = target_questions.some(
              question => String(question.check_mode).toLowerCase() === 'manual'
            );

            return {
              association_id: item.association_id,
              manual_association_id: item.association_id,
              id: item.id,
              title: item.name,
              is_open: true,
              is_completed: item.is_completed === true,
              check_mode: item.check_mode || (has_manual_mode ? 'manual' : 'automatic'),
              manual_checked: item.manual_checked === true,
              auto_checked: item.auto_checked === true,
              completed_by: item.completed_by || null,
              is_evaluated: item.is_evaluated || false,
              evaluation_result_id: item.evaluation_result_id || null,
              evaluation_result: item.evaluation_result ?? null,
              requirement_types: item.requirement_types,
              requirement_statuses: item.requirement_statuses || [],
              target_questions
            };
          });

          const check_mode_requests = mapped_items.map(async (item) => {
            const target_association_ids = (item.target_questions || [])
              .map(target_question => target_question.association_id)
              .filter(Boolean);

            let manual_association_id = null;
            for (const target_association_id of target_association_ids) {
              const target_mode = await this.get_check_mode_for_association(target_association_id);
              if (target_mode === 'manual') {
                manual_association_id = target_association_id;
                break;
              }
            }

            const fetched_mode = manual_association_id
              ? 'manual'
              : await this.get_check_mode_for_association(item.association_id);

              const loaded_check_items = {
              ...item,
              manual_association_id: manual_association_id || item.association_id,
              check_mode: fetched_mode || item.check_mode || 'automatic',
            };
            return loaded_check_items
          });

          this.checklist_items = await Promise.all(check_mode_requests);

          this.$emit('compliance-loaded', this.checklist_items.length > 0);

        } else {
          this.show_error('Failed to load checklist items');
        }
      } catch (error) {
        this.show_error('Error loading checklist items');
      } finally {
        this.is_loading = false;
      }
    },

    get_applicable_questions(questions) {
      return (questions || []).filter(q => q.is_applicable);
    },

    get_not_applicable_questions(questions) {
      return (questions || []).filter(q => !q.is_applicable);
    },

    get_answered_applicable_count(questions) {
      return this.get_applicable_questions(questions).filter(q => q.is_answered).length;
    },


    get_requirement_type_class(req_status) {
      // Base classes for all badges
      const base_classes = 'border border-2';

      if (!req_status.is_met) {
        return `${base_classes} bg-danger bg-opacity-10 border-danger text-danger`;
      }

      const type_lower = req_status.type.toLowerCase().replace(/\s+/g, '-');

      const class_map = {
        'answered': `${base_classes} bg-success bg-opacity-10 border-success text-success`,
        'manual-verification': `${base_classes} bg-info bg-opacity-10 border-info text-info`,
        'automatic-verification': `${base_classes} bg-primary bg-opacity-10 border-primary text-primary`,
        'requested-info-feedback': `${base_classes} bg-warning bg-opacity-10 border-warning text-warning`,
        'requested-information-feedback': `${base_classes} bg-warning bg-opacity-10 border-warning text-warning`,
      };

      if (Reflect.has(class_map, type_lower)) {
        return Reflect.get(class_map, type_lower);
      }
      return `${base_classes} bg-secondary bg-opacity-10 border-secondary text-secondary`;
    },

    get_requirement_type_icon(req_status) {
      const type_lower = req_status.type.toLowerCase().replace(/\s+/g, '-');

      const icon_map = {
        'answered': 'bi bi-chat-check-fill',
        'manual-verification': 'bi bi-shield-check',
        'automatic-verification': 'bi bi-shield-fill-check',
        'requested-info-feedback': 'bi bi-chat-dots-fill',
        'requested-information-feedback': 'bi bi-chat-dots-fill',
      };

      if (Reflect.has(icon_map, type_lower)) {
        return Reflect.get(icon_map, type_lower);
      }
      return 'bi bi-tag-fill';
    },

    get_verification_type(req_status) {
      // For verification requirements, get the verification type from backend
      const type_lower = req_status.type.toLowerCase();
      if ((type_lower.includes('verification')) && req_status.verification_type) {
        return `(${req_status.verification_type})`;
      }
      return '';
    },

    preview_file(file_url) {
      this.preview_url = file_url;
      this.preview_file_name = this.get_file_name_from_url(file_url);
      this.preview_modal = true;
    },

    get_file_name_from_url(url) {
      const decoded = decodeURIComponent(url);
      const clean = decoded.split('?')[0];
      return clean.split('/').pop();
    },


    close_the_file_preview() {
      this.preview_modal = false;
      this.preview_url = null;
      this.preview_file_name = '';
    },


    toggle_section(index) {
      const item = Reflect.get(this.checklist_items, index);
      if (item) {
        Reflect.set(item, 'is_open', !Reflect.get(item, 'is_open'));
      }
    },

    update_completion_status() {
      this.checklist_items.forEach(item => {
        const check_mode = String(item?.check_mode || '').toLowerCase();
        const requirement_statuses = Array.isArray(item?.requirement_statuses)
          ? item.requirement_statuses
          : [];
        const all_requirements_met = requirement_statuses.length > 0
          ? requirement_statuses.every(req_status => req_status?.is_met === true)
          : this.is_item_question_complete(item);

        const manual_checked = this.is_manual_checked(item);
        const auto_checked = item?.auto_checked === true || (check_mode !== 'manual' && all_requirements_met);
        const is_completed = check_mode === 'manual' ? manual_checked : auto_checked;

        Reflect.set(item, 'manual_checked', manual_checked);
        Reflect.set(item, 'auto_checked', auto_checked);
        Reflect.set(item, 'is_completed', is_completed);
      });
    },

    async re_run_checks() {
      if (this.completion_percentage !== 100) {
        this.show_error('Please complete all checklist items before re-running checks.');
        return;
      }

      this.is_rerunning_checks = true;

      try {
        // DON'T send a specific rule_id - send null or undefined
        // This tells the backend "process ALL rules for this entity"
        const response = await axios.post(START_ONGOING_COMPLIANCE, {
          entity_profile_id: this.entity_profile_id,
          // compliance_rule_id: null  // Either don't send it at all, or send null
        }, {
          headers: get_headers()
        });

        if (response.data.status === 'success' || response.data.status === 'partial_success') {
          await this.load_checklist_items();

          // Show a more detailed message based on how many rules were processed
          const results = response.data.results || [];
          const errors = response.data.errors || [];

          if (errors.length > 0) {
            this.show_warning(`Processed ${results.length} rules, but ${errors.length} failed. Check logs.`);
          } else {
            this.show_success(`Successfully triggered ${results.length} compliance rules.`);
          }

          this.$emit('checks-rerun', {
            entity_profile_id: this.entity_profile_id,
            results: response.data.results
          });
        } else {
          this.show_error(response.data.message || 'Failed to trigger checks');
        }
      } catch (err) {
        const error_msg = err.response?.data?.message || 'Server connection error';
        this.show_error(`Error: ${error_msg}`);
      } finally {
        this.is_rerunning_checks = false;
      }
    },

    get_answered_count(questions) {
      return this.get_applicable_questions(questions).filter(question => question.is_answered).length;
    },

    format_answer(answer) {
      if (answer === null || answer === undefined) return 'N/A';
      if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';
      if (typeof answer === 'string' && answer.length > 50) {
        return answer.substring(0, 50) + '...';
      }
      return String(answer);
    },

    async submit_evaluation_result(checklist_item_association_id, result) {
      const payload = {
        checklist_item_association_id,
        entity_profile_id: this.entity_profile_id,
        result
      };

      try {
        const response = await axios.post(CREATE_CHECKLIST_EVALUATION_RESULT, payload, {
          headers: get_headers()
        });

        if (response.data.status === 'success') {
          const index = this.checklist_items.findIndex(
            i => Number(i.association_id) === Number(checklist_item_association_id)
          );
          if (index !== -1) {
            // safest: replace the object so Vue re-renders for sure
            const updated = {
              ...Reflect.get(this.checklist_items, index),
              is_evaluated: true,
              evaluation_result_id: Reflect.get(response.data, 'result_id') || 
                Reflect.get(Reflect.get(this.checklist_items, index), 'evaluation_result_id'),
            };
            this.checklist_items.splice(index, 1, updated);
          }
          this.show_success(
          result
            ? 'Checklist item marked as passed successfully'
            : 'Checklist item unchecked successfully'
        );
        await this.load_checklist_items();
        } else {
          this.show_error(response.data.message || 'Failed to submit evaluation result');
        }
      } catch (e) {
        this.show_error('Failed to submit evaluation result');
      }
    }

  }
};
